/**
 * Data Corrections list and add/edit. Manager-only; feature-gated by data_corrections_enabled.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import ContentHeader from '../components/ContentHeader';
import { dataCorrectionsAPI, staffAPI, clientAPI, templateAPI, documentAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import './DataCorrectionsList.css';

export default function DataCorrectionsList() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('Open');
  const [staffFilter, setStaffFilter] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchDebounced, setClientSearchDebounced] = useState('');
  const [openDateFrom, setOpenDateFrom] = useState('');
  const [openDateTo, setOpenDateTo] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const canManage = hasPermission('data_corrections.manage');

  // Filter popover: which column's popover is open ('client' | 'openDate' | null)
  const [openFilterPopover, setOpenFilterPopover] = useState(null);
  const [draftClientSearch, setDraftClientSearch] = useState('');
  const [draftOpenDateFrom, setDraftOpenDateFrom] = useState('');
  const [draftOpenDateTo, setDraftOpenDateTo] = useState('');
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 260 });
  const filterButtonRefs = useRef({});
  const [sortKey, setSortKey] = useState('open_date');
  const [sortDir, setSortDir] = useState('desc');

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (statusFilter && ['Open', 'Approved', 'Denied'].includes(statusFilter)) params.status = statusFilter;
      if (staffFilter) params.staff_id = staffFilter;
      if (clientSearchDebounced) params.q = clientSearchDebounced;
      if (openDateFrom) params.open_date_from = openDateFrom;
      if (openDateTo) params.open_date_to = openDateTo;
      const res = await dataCorrectionsAPI.list(params);
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load data corrections');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, staffFilter, clientSearchDebounced, openDateFrom, openDateTo]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const t = setTimeout(() => setClientSearchDebounced(clientSearch), 300);
    return () => clearTimeout(t);
  }, [clientSearch]);

  useEffect(() => {
    if (!saveMessage) return;
    const t = setTimeout(() => setSaveMessage(null), 5000);
    return () => clearTimeout(t);
  }, [saveMessage]);

  useEffect(() => {
    staffAPI.getAll({ active: 'true' }).then((res) => {
      const data = res.data?.data ?? res.data;
      setStaffList(Array.isArray(data) ? data : []);
    }).catch(() => setStaffList([]));
  }, []);

  // Close filter popover on outside click or Escape
  useEffect(() => {
    if (!openFilterPopover) return undefined;
    const onPointerDown = (e) => {
      if (e.target?.closest?.('.dc-header-filter-wrap') || e.target?.closest?.('.dc-filter-popover-portal')) return;
      setOpenFilterPopover(null);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpenFilterPopover(null); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openFilterPopover]);

  // Position popover below the trigger button
  useEffect(() => {
    if (!openFilterPopover) return undefined;
    const el = filterButtonRefs.current[openFilterPopover];
    if (!el) return undefined;
    const rect = el.getBoundingClientRect();
    const width = openFilterPopover === 'openDate' ? 220 : 260;
    const pad = 8;
    const maxLeft = window.innerWidth - width - pad;
    const left = Math.min(Math.max(pad, rect.left + window.scrollX), maxLeft);
    setPopoverPosition({ top: rect.bottom + window.scrollY + 6, left, width });
  }, [openFilterPopover]);

  const openFilter = (key) => {
    if (openFilterPopover === key) {
      setOpenFilterPopover(null);
      return;
    }
    if (key === 'client') {
      setDraftClientSearch(clientSearch);
    } else if (key === 'openDate') {
      setDraftOpenDateFrom(openDateFrom);
      setDraftOpenDateTo(openDateTo);
    }
    setOpenFilterPopover(key);
  };

  const applyFilter = async (key) => {
    if (key === 'client') {
      const val = (draftClientSearch || '').trim();
      setClientSearch(val);
      setClientSearchDebounced(val);
      setOpenFilterPopover(null);
      // Refetch immediately with the applied value so filtering works reliably
      try {
        setLoading(true);
        setError(null);
        const params = {};
        if (statusFilter && ['Open', 'Approved', 'Denied'].includes(statusFilter)) params.status = statusFilter;
        if (staffFilter) params.staff_id = staffFilter;
        if (val) params.q = val;
        if (openDateFrom) params.open_date_from = openDateFrom;
        if (openDateTo) params.open_date_to = openDateTo;
        const res = await dataCorrectionsAPI.list(params);
        setList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load data corrections');
        setList([]);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (key === 'openDate') {
      const from = (draftOpenDateFrom || '').trim();
      const to = (draftOpenDateTo || '').trim();
      setOpenDateFrom(from);
      setOpenDateTo(to);
      setOpenFilterPopover(null);
      try {
        setLoading(true);
        setError(null);
        const params = {};
        if (statusFilter && ['Open', 'Approved', 'Denied'].includes(statusFilter)) params.status = statusFilter;
        if (staffFilter) params.staff_id = staffFilter;
        if (clientSearchDebounced) params.q = clientSearchDebounced;
        if (from) params.open_date_from = from;
        if (to) params.open_date_to = to;
        const res = await dataCorrectionsAPI.list(params);
        setList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load data corrections');
        setList([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const clearFilter = (key) => {
    if (key === 'client') {
      setClientSearch('');
      setClientSearchDebounced('');
      setDraftClientSearch('');
    } else if (key === 'openDate') {
      setOpenDateFrom('');
      setOpenDateTo('');
      setDraftOpenDateFrom('');
      setDraftOpenDateTo('');
    }
    setOpenFilterPopover(null);
  };

  const clientName = (row) => [row.client_last_name, row.client_first_name].filter(Boolean).join(', ') || '—';
  const staffName = (row) => [row.staff_first_name, row.staff_last_name].filter(Boolean).join(' ') || '—';

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'open_date' ? 'desc' : 'asc');
    }
  };

  const sortedList = useMemo(() => {
    const rows = Array.isArray(list) ? [...list] : [];
    const mul = sortDir === 'asc' ? 1 : -1;
    const cmpStr = (a, b) => mul * String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });
    rows.sort((a, b) => {
      switch (sortKey) {
        case 'client': {
          const c = cmpStr(a.client_last_name, b.client_last_name);
          if (c !== 0) return c;
          return cmpStr(a.client_first_name, b.client_first_name);
        }
        case 'staff': {
          const c = cmpStr(a.staff_last_name, b.staff_last_name);
          if (c !== 0) return c;
          return cmpStr(a.staff_first_name, b.staff_first_name);
        }
        case 'status':
          return cmpStr(a.status, b.status);
        case 'open_date': {
          const da = a.open_date ? new Date(a.open_date).getTime() : 0;
          const db = b.open_date ? new Date(b.open_date).getTime() : 0;
          if (da !== db) return mul * (da < db ? -1 : 1);
          return mul * ((a.id || 0) - (b.id || 0));
        }
        default:
          return 0;
      }
    });
    return rows;
  }, [list, sortKey, sortDir]);

  return (
    <div className="data-corrections-list-page">
      <ContentHeader
        title="Data Corrections"
        breadcrumbs={[{ label: 'Lists', to: tenantPath('/lists') }, { label: 'Data Corrections' }]}
      />
      {error && <p className="data-corrections-error">{error}</p>}
      {saveMessage && <p className="data-corrections-save-message">{saveMessage}</p>}

      <div className="data-corrections-toolbar">
        <span />
        {canManage && (
          <button type="button" className="btn btn-primary" onClick={() => { setShowAddForm(true); setEditingId(null); setEditingRow(null); }}>
            + Add Data Correction
          </button>
        )}
      </div>

      <div className="data-corrections-list-info">
        {loading && list.length === 0
          ? 'Loading...'
          : list.length === 0
            ? 'No data corrections'
            : list.length === 1
              ? '1 data correction'
              : `${list.length} data corrections`}
      </div>

      {!loading && (
        <div className="data-corrections-table-wrap">
          <table className="data-corrections-table">
            <thead>
              <tr className="data-corrections-header-labels">
                <th scope="col">
                  <button
                    type="button"
                    className="dc-sort-header"
                    onClick={() => handleSortClick('client')}
                    aria-sort={sortKey === 'client' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    Client
                    {sortKey === 'client' && (
                      <span className="dc-sort-arrow" aria-hidden="true">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </button>
                </th>
                <th scope="col">
                  <button
                    type="button"
                    className="dc-sort-header"
                    onClick={() => handleSortClick('staff')}
                    aria-sort={sortKey === 'staff' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    Staff
                    {sortKey === 'staff' && (
                      <span className="dc-sort-arrow" aria-hidden="true">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </button>
                </th>
                <th scope="col">
                  <button
                    type="button"
                    className="dc-sort-header"
                    onClick={() => handleSortClick('open_date')}
                    aria-sort={sortKey === 'open_date' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    Open date
                    {sortKey === 'open_date' && (
                      <span className="dc-sort-arrow" aria-hidden="true">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </button>
                </th>
                <th scope="col">
                  <button
                    type="button"
                    className="dc-sort-header"
                    onClick={() => handleSortClick('status')}
                    aria-sort={sortKey === 'status' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    Status
                    {sortKey === 'status' && (
                      <span className="dc-sort-arrow" aria-hidden="true">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </button>
                </th>
              </tr>
              <tr className="data-corrections-filter-row">
                <th>
                  <div className="dc-header-filter-wrap">
                    <button
                      type="button"
                      ref={(el) => { filterButtonRefs.current.client = el; }}
                      className="dc-filter-trigger"
                      onClick={(e) => { e.stopPropagation(); openFilter('client'); }}
                      aria-label="Filter by client"
                      title="Filter by client"
                    >
                      <svg className="dc-filter-trigger-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </th>
                <th>
                  <select
                    className="data-corrections-header-filter"
                    value={staffFilter}
                    onChange={(e) => setStaffFilter(e.target.value)}
                    aria-label="Filter by staff"
                  >
                    <option value="">All</option>
                    {staffList.map((s) => (
                      <option key={s.id} value={s.id}>{[s.last_name, s.first_name].filter(Boolean).join(', ') || s.id}</option>
                    ))}
                  </select>
                </th>
                <th>
                  <div className="dc-header-filter-wrap">
                    <button
                      type="button"
                      ref={(el) => { filterButtonRefs.current.openDate = el; }}
                      className="dc-filter-trigger"
                      onClick={(e) => { e.stopPropagation(); openFilter('openDate'); }}
                      aria-label="Filter by open date range"
                      title="Filter by open date"
                    >
                      <svg className="dc-filter-trigger-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </th>
                <th>
                  <select
                    className="data-corrections-header-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Filter by status"
                  >
                    <option value="">All</option>
                    <option value="Open">Open</option>
                    <option value="Approved">Approved</option>
                    <option value="Denied">Denied</option>
                  </select>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedList.length === 0 ? (
                <tr><td colSpan={4}>No data corrections found.</td></tr>
              ) : (
                sortedList.map((row) => (
                  <tr
                    key={row.id}
                    className={canManage ? 'clickable-row' : ''}
                    onClick={canManage ? () => { setEditingId(row.id); setEditingRow(row); setShowAddForm(false); } : undefined}
                    role={canManage ? 'button' : undefined}
                    tabIndex={canManage ? 0 : undefined}
                    onKeyDown={canManage ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingId(row.id); setEditingRow(row); setShowAddForm(false); } } : undefined}
                  >
                    <td>{clientName(row)}</td>
                    <td>{staffName(row)}</td>
                    <td>{formatDisplayDate(row.open_date)}</td>
                    <td>{row.status || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Filter popovers (stacked: icon opens dropdown with From/To or search + Apply/Clear) */}
      {openFilterPopover === 'client' &&
        createPortal(
          <div
            className="dc-filter-popover dc-filter-popover-portal"
            style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px`, width: `${popoverPosition.width}px` }}
          >
            <label className="dc-filter-popover-label">
              <span>Search client name</span>
              <input
                type="text"
                className="dc-filter-input"
                placeholder="Search client..."
                value={draftClientSearch}
                onChange={(e) => setDraftClientSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilter('client')}
              />
            </label>
            <div className="dc-filter-popover-actions">
              <button type="button" className="btn btn-secondary" onClick={() => clearFilter('client')}>Clear</button>
              <button type="button" className="btn btn-primary" onClick={() => applyFilter('client')}>Apply</button>
            </div>
          </div>,
          document.body
        )}
      {openFilterPopover === 'openDate' &&
        createPortal(
          <div
            className="dc-filter-popover dc-filter-popover-portal"
            style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px`, width: `${popoverPosition.width}px` }}
          >
            <div className="dc-filter-date-range">
              <label className="dc-filter-date-field">
                <span>From</span>
                <input
                  type="date"
                  className="dc-filter-input"
                  value={draftOpenDateFrom}
                  onChange={(e) => setDraftOpenDateFrom(e.target.value)}
                />
              </label>
              <label className="dc-filter-date-field">
                <span>To</span>
                <input
                  type="date"
                  className="dc-filter-input"
                  value={draftOpenDateTo}
                  onChange={(e) => setDraftOpenDateTo(e.target.value)}
                />
              </label>
            </div>
            <div className="dc-filter-popover-actions">
              <button type="button" className="btn btn-secondary" onClick={() => clearFilter('openDate')}>Clear</button>
              <button type="button" className="btn btn-primary" onClick={() => applyFilter('openDate')}>Apply</button>
            </div>
          </div>,
          document.body
        )}

      {showAddForm && (
        <DataCorrectionForm
          mode="add"
          staffList={staffList}
          onSave={(msg) => { setShowAddForm(false); loadList(); if (msg) setSaveMessage(msg); }}
          onCancel={() => setShowAddForm(false)}
        />
      )}
      {editingId && editingRow && !showAddForm && (
        <DataCorrectionForm
          mode="edit"
          id={editingId}
          initial={editingRow}
          staffList={staffList}
          onSave={(msg) => { setEditingId(null); setEditingRow(null); loadList(); if (msg) setSaveMessage(msg); }}
          onCancel={() => { setEditingId(null); setEditingRow(null); }}
        />
      )}
    </div>
  );
}

const CLIENT_SEARCH_DEBOUNCE_MS = 300;

function DataCorrectionForm({ mode, id, initial, staffList, onSave, onCancel }) {
  const [clientId, setClientId] = useState(initial?.client_id ?? '');
  const [clientName, setClientName] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientSearchRef = useRef(null);
  const [staffId, setStaffId] = useState(initial?.staff_id ?? '');
  const [openDate, setOpenDate] = useState(initial?.open_date ? (initial.open_date.slice?.(0, 10) || initial.open_date) : new Date().toISOString().slice(0, 10));
  const [resolutionDate, setResolutionDate] = useState(initial?.resolution_date ? (initial.resolution_date.slice?.(0, 10) || initial.resolution_date) : '');
  const [status, setStatus] = useState(initial?.status ?? 'Open');
  const [ticketNumber, setTicketNumber] = useState(initial?.ticket_number ?? '');
  const [issue, setIssue] = useState(initial?.issue ?? '');
  const [comment, setComment] = useState(initial?.comment ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [dcTemplates, setDcTemplates] = useState([]);
  const [dcTemplatesLoading, setDcTemplatesLoading] = useState(false);
  const [dcTemplatesErr, setDcTemplatesErr] = useState(null);
  const [selectedDcTemplateId, setSelectedDcTemplateId] = useState('');
  const [generatingDcDoc, setGeneratingDcDoc] = useState(false);
  const [dcGenErr, setDcGenErr] = useState(null);

  useEffect(() => {
    if (mode !== 'edit' || !initial?.client_id) {
      setDcTemplates([]);
      setDcTemplatesErr(null);
      setSelectedDcTemplateId('');
      return undefined;
    }
    setDcTemplatesLoading(true);
    setDcTemplatesErr(null);
    templateAPI.getAll({ docType: 'DataCorrection', clientId: initial.client_id })
      .then((res) => {
        const list = res.data?.templates ?? res.data?.data ?? [];
        const sorted = Array.isArray(list)
          ? [...list].sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || ''), undefined, { sensitivity: 'base' }))
          : [];
        setDcTemplates(sorted);
        setSelectedDcTemplateId((prev) => {
          if (prev && sorted.some((t) => t.id === prev)) return prev;
          return sorted[0]?.id || '';
        });
      })
      .catch((e) => {
        setDcTemplates([]);
        setDcTemplatesErr(e.response?.data?.error || e.message || 'Could not load document templates');
      })
      .finally(() => setDcTemplatesLoading(false));
    return undefined;
  }, [mode, initial?.client_id]);

  useEffect(() => {
    if (mode === 'edit' && initial?.client_id && !clientName) {
      clientAPI.getById(initial.client_id).then((res) => {
        const c = res.data?.data ?? res.data;
        if (c) setClientName([c.last_name, c.first_name].filter(Boolean).join(', ') || '');
      }).catch(() => {});
    }
  }, [mode, initial?.client_id, clientName]);

  const runClientSearch = useCallback((q) => {
    if (!q || String(q).trim().length < 2) {
      setClientResults([]);
      return;
    }
    setClientLoading(true);
    clientAPI.getAll({ q: String(q).trim(), page: 1, pageSize: 500 })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setClientResults(Array.isArray(data) ? data : []);
      })
      .catch(() => setClientResults([]))
      .finally(() => setClientLoading(false));
  }, []);

  useEffect(() => {
    if (mode !== 'add') return;
    const t = setTimeout(() => runClientSearch(clientSearch), CLIENT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [mode, clientSearch, runClientSearch]);

  const openClientDropdown = () => setClientDropdownOpen(true);
  const closeClientDropdown = () => setTimeout(() => setClientDropdownOpen(false), 200);

  const selectClient = (client) => {
    setClientId(client.id);
    setClientName([client.last_name, client.first_name].filter(Boolean).join(', ') || '');
    setClientSearch('');
    setClientResults([]);
    setClientDropdownOpen(false);
    setErr(null);
  };

  const clearSelectedClient = () => {
    setClientId('');
    setClientName('');
    setClientSearch('');
    setClientResults([]);
    setErr(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    const cId = mode === 'edit' ? initial.client_id : clientId;
    const sId = staffId ? parseInt(staffId, 10) : null;
    if (!cId || !sId || !openDate || !status) {
      setErr('Please select a client, staff, and fill required fields.');
      return;
    }
    if ((status === 'Approved' || status === 'Denied') && !resolutionDate) {
      setErr('Resolution date is required when status is Approved or Denied.');
      return;
    }
    setSubmitting(true);
    try {
      let saveMsg = null;
      if (mode === 'add') {
        await dataCorrectionsAPI.create({
          client_id: cId,
          staff_id: sId,
          open_date: openDate,
          resolution_date: resolutionDate || null,
          status,
          ticket_number: ticketNumber.trim() || null,
          issue: issue.trim() || '',
          comment: comment.trim() || null,
        });
      } else {
        const res = await dataCorrectionsAPI.update(id, {
          resolution_date: resolutionDate || null,
          status,
          ticket_number: ticketNumber.trim() || null,
          issue: issue.trim() || '',
          comment: comment.trim() || null,
        });
        if (res.data?.inboxNotificationSent) {
          const name = res.data.notifiedStaffName;
          saveMsg = `Saved. Inbox notification sent to ${name || 'the service coordinator'}.`;
        } else {
          saveMsg = 'Saved.';
        }
      }
      onSave(saveMsg);
    } catch (err) {
      setErr(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateDataCorrectionDoc = async () => {
    if (!id || !selectedDcTemplateId) return;
    const template = dcTemplates.find((t) => t.id === selectedDcTemplateId);
    setDcGenErr(null);
    setGeneratingDcDoc(true);
    try {
      const response = await documentAPI.generateForDataCorrection(id, selectedDcTemplateId);
      const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
      if (contentType.includes('application/json')) {
        const errorData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        throw new Error(errorData?.error || errorData?.message || 'Failed to generate document');
      }
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = template ? `${template.displayName}.docx` : 'data-correction.docx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match) filename = match[1].replace(/['"]/g, '');
      }
      const blobType = contentType && !contentType.includes('json')
        ? contentType
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const blob = new Blob([response.data], { type: blobType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDcGenErr(e.response?.data?.error || e.message || 'Failed to generate document');
    } finally {
      setGeneratingDcDoc(false);
    }
  };

  return (
    <div className="data-corrections-form-overlay">
      <div className="data-corrections-form-card">
        <h3>{mode === 'add' ? 'Add Data Correction' : 'Edit Data Correction'}</h3>
        {err && <p className="data-corrections-form-error">{err}</p>}
        <form onSubmit={handleSubmit}>
          {mode === 'add' ? (
            <>
              <div className="data-corrections-form-row">
                <label>Client</label>
                {clientId && clientName ? (
                  <div className="data-corrections-form-client-selected">
                    <span className="data-corrections-form-client-name">{clientName}</span>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={clearSelectedClient}>Change client</button>
                  </div>
                ) : (
                  <div className="data-corrections-form-client-search-wrap" ref={clientSearchRef}>
                    <input
                      type="text"
                      className="data-corrections-form-input"
                      placeholder="Search by client name or EI number..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      onFocus={openClientDropdown}
                      onBlur={closeClientDropdown}
                      autoComplete="off"
                      aria-expanded={clientDropdownOpen}
                      aria-controls="dc-client-results"
                      aria-autocomplete="list"
                    />
                    {clientLoading && <span className="data-corrections-form-search-loading" aria-live="polite">Searching…</span>}
                    {clientDropdownOpen && (clientSearch.length >= 2 || clientResults.length > 0) && (
                      <ul id="dc-client-results" className="data-corrections-form-client-results" role="listbox">
                        {clientResults.length === 0 && !clientLoading && (
                          <li className="data-corrections-form-client-result-item data-corrections-form-client-no-results">Type at least 2 characters to search.</li>
                        )}
                        {clientResults.map((c) => (
                          <li
                            key={c.id}
                            role="option"
                            className="data-corrections-form-client-result-item"
                            onPointerDown={(e) => { e.preventDefault(); selectClient(c); }}
                          >
                            {[c.last_name, c.first_name].filter(Boolean).join(', ') || c.id}
                            {c.ei_number && ` · ${c.ei_number}`}
                            {c.date_of_birth && ` · DOB ${formatDisplayDate(c.date_of_birth)}`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="data-corrections-form-row">
                <label>Staff (SC)</label>
                <select value={staffId} onChange={(e) => setStaffId(e.target.value)} required>
                  <option value="">Select…</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>{[s.last_name, s.first_name].filter(Boolean).join(', ') || s.id}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div className="data-corrections-form-row">
              <span className="data-corrections-form-client-line">Client: {[initial?.client_last_name, initial?.client_first_name].filter(Boolean).join(', ') || clientName || initial?.client_id || '—'}</span>
            </div>
          )}
          <div className="data-corrections-form-grid-2">
            <div className="data-corrections-form-field-col">
              <label htmlFor="dc-form-open-date">Open date</label>
              <input
                id="dc-form-open-date"
                type="date"
                value={openDate}
                onChange={(e) => setOpenDate(e.target.value)}
                required
                readOnly={mode === 'edit'}
              />
            </div>
            <div className="data-corrections-form-field-col">
              <label htmlFor="dc-form-status">Status</label>
              <select id="dc-form-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="Open">Open</option>
                <option value="Approved">Approved</option>
                <option value="Denied">Denied</option>
              </select>
            </div>
            <div className="data-corrections-form-field-col">
              <label htmlFor="dc-form-resolution-date">Resolution date</label>
              <input
                id="dc-form-resolution-date"
                type="date"
                value={resolutionDate}
                onChange={(e) => setResolutionDate(e.target.value)}
                required={status === 'Approved' || status === 'Denied'}
              />
            </div>
            <div className="data-corrections-form-field-col">
              <label htmlFor="dc-form-ticket">Ticket number</label>
              <input
                id="dc-form-ticket"
                type="text"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="data-corrections-form-row">
            <label htmlFor="dc-form-issue">Issue</label>
            <textarea id="dc-form-issue" value={issue} onChange={(e) => setIssue(e.target.value)} rows={3} required={mode === 'add'} />
          </div>
          <div className="data-corrections-form-row">
            <label htmlFor="dc-form-comment">Comment</label>
            <textarea id="dc-form-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} />
          </div>
          <div className="data-corrections-form-footer-row">
            {mode === 'edit' && (
              <div className="data-corrections-form-doc-gen-footer">
                <label className="data-corrections-form-doc-gen-label" htmlFor="dc-doc-template">Document template</label>
                <div className="data-corrections-form-doc-gen-inline">
                  <select
                    id="dc-doc-template"
                    className="data-corrections-form-template-select"
                    value={selectedDcTemplateId}
                    onChange={(e) => setSelectedDcTemplateId(e.target.value)}
                    disabled={dcTemplatesLoading || dcTemplates.length === 0}
                  >
                    {dcTemplatesLoading && <option value="">Loading…</option>}
                    {!dcTemplatesLoading && dcTemplates.length === 0 && (
                      <option value="">No templates configured</option>
                    )}
                    {dcTemplates.map((t) => (
                      <option key={t.id} value={t.id}>{t.displayName || t.fileName || t.id}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="data-corrections-form-generate-icon"
                    disabled={!selectedDcTemplateId || generatingDcDoc || dcTemplatesLoading}
                    onClick={handleGenerateDataCorrectionDoc}
                    aria-busy={generatingDcDoc}
                    title={generatingDcDoc ? 'Generating…' : 'Generate'}
                    aria-label={generatingDcDoc ? 'Generating…' : 'Generate document'}
                  >
                    {generatingDcDoc ? (
                      <span className="data-corrections-form-generate-spinner" aria-hidden="true" />
                    ) : (
                      <svg className="data-corrections-form-generate-play" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    )}
                  </button>
                </div>
                {dcTemplatesLoading && <p className="data-corrections-form-doc-gen-hint">Loading templates…</p>}
                {dcTemplatesErr && <p className="data-corrections-form-error">{dcTemplatesErr}</p>}
                {dcGenErr && <p className="data-corrections-form-error">{dcGenErr}</p>}
                {!dcTemplatesLoading && !dcTemplatesErr && dcTemplates.length === 0 && (
                  <p className="data-corrections-form-doc-gen-hint">
                    Register templates in Document templates and set category to &quot;Data correction letters&quot;,
                    or category Other with prompt tag &quot;Data correction letter&quot; so they appear here.
                  </p>
                )}
              </div>
            )}
            <div className="data-corrections-form-footer-save">
              <button type="button" className="btn btn-cancel" onClick={onCancel}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
